/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Container, createStyles, List, ListItem, ListItemText, Paper, Theme, TablePagination } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState, redirect, setCurrentFacilityAction, syncSystem } from "../../redux";
import { useHistory } from 'react-router';
import LoadingIndicator from './form/components/LoadingIndicator';
import { Wmp } from "../../db/stores/wmps";
import moment from "moment";

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      [theme.breakpoints.up("xs")]: {
        overflowY: "auto",
      },
      WebkitOverflowScrolling: "touch",
    },
    container: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3),
    },
    list: {
      display: "flex",
      flexDirection: "column",
    },
    listing: {
      backgroundColor: "#fff",
      display: "flex"
    },
    listEl: {
      paddingBottom: (props: WmpListProperties) => props.preview ? 0 : '10px',
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
  });
});

export const selectWmps = (state: AppState) => state.wmps.items;
export const selectWmpsFetching = (state: AppState) => state.wmps.fetching;

interface WmpListProperties {
  limit?: number;
  preview?: boolean;
  classes?: Partial<Record<'root' | 'list' | 'loading', string>>
 }

const WmpList: FC<WmpListProperties> = (props = { preview: false }) => {
  const classes = useStyles(props);
  const wmps = useSelector(selectWmps);
  const fetchingWmps = useSelector(selectWmpsFetching);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(
    function refresh() {
      dispatch(setCurrentFacilityAction(undefined));
      dispatch(syncSystem());
    },
    [dispatch]
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalWmps, setTotalWmps] = useState(0);

  useEffect(() => {
    setTotalWmps(Object.keys(wmps).length);
  }, [wmps])

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function handleClick(wmp: Wmp) {
    return function() {
      dispatch(setCurrentFacilityAction(wmp.facilityId));
      dispatch(redirect(`/wmps/${wmp.id}`, history));
    }
  }

  const content =
    totalWmps > 0 ? (
      <>
        {Object.values(wmps).map((wmp: Wmp) => (
            <ListItem
              key={wmp.id}
              className={classes.loading}
              button
              onClick={handleClick(wmp)}
            >
              <ListItemText primary={wmp.facility} secondary={ 'Date Initiated: ' + moment(wmp.dateInitiated).format('Y-MM-DD')} />
            </ListItem>
        ))}
      </>
    ) : (
        <ListItem>
          <ListItemText primary="No WMPs yet" />
        </ListItem>
      );

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.container}>
        <Paper elevation={1} className={classes.list}>
          <List className={classes.listEl}>
            { (fetchingWmps) ? (
              <LoadingIndicator
                classes={{
                  container: classes.loading,
                }}
              >
                Loading WMPs
              </LoadingIndicator>
            ) : content}
          </List>
          { !props.preview  && (
            <TablePagination
                count={totalWmps}
                rowsPerPage={rowsPerPage}
                component="div"
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default WmpList;
